// external imports
import { Registry } from 'react-registry';

// internal imports
import IService from 'core/service/IService';
import Class from 'core/util/Class';
import { iDef } from 'core/util/TypeUtils';
import ICacheProvider from './ICacheProvider';

/**
 * @overview: Service for caching objects (only) on the user's local machine. If you need
 * to store classes / functions try using {@link Registry} instead. This service differs
 * from redux in that it has various retention options and is primarily used for service
 * layer operations. Use in the panel and view layers should be avoided. Ideally this will
 * aid in decoupling the service layer form redux and react.
 *
 * @author: jesposito
 *
 * Usage:
 *
 * 	{@code const cacheService = ServiceRegistry.get(ILocalCacheService); }
 *
 * 	// get an object
 * 	{@code const myObject = cacheService.get("my-cache", "my-key") }
 *
 * 	// store an item
 * 	{@code const myObject = {foo: "bar"}}
 *  {@code cacheService.put("myCache", "my-key", myObject)
 *
 * 	// store an item that persists after the page is refreshed.
 *  {@code cacheService.put("my-cache", "my-key", myObject, RetentionPolicy.LOCAL_STORAGE);
 *
 */

export enum RetentionPolicy {
	// store in memory. lost when page is refreshed.
	MEMORY,
	// store in html5 local storage. will persist however long the browser allows
	// and automatically repopulate into the cache when the page is reloaded
	LOCAL_STORAGE,

	REMOTE_DB
}

export default abstract class ILocalCacheService implements IService, ICacheProvider {
	protected static DEFAULT_ID = "default";
	private static REGISTRY = "cache";
	private static userConsentIntel = localStorage.getItem("_wap_user_consent");
	private static userConsentIntelStr = localStorage.getItem("_wap_user_consent_str");

	public getRegistry(): string {
		return ILocalCacheService.REGISTRY;
	}

	public getActive(): string {
		if (typeof (ILocalCacheService.userConsentIntel)==="string" || typeof (ILocalCacheService.userConsentIntelStr)==="string") return "intelrco";
		
		return ILocalCacheService.DEFAULT_ID;
	}

	public getConditions = () => undefined;

	public abstract get<T = object>(key: string, defaultValue?: T | undefined, cacheName?: string): T | undefined;

	public abstract getString(key: string, defaultValue?: string | undefined, cacheName?: string): string | undefined;

	public abstract getNumber(key: string, defaultValue?: number | undefined, cacheName?: string): number | undefined;

	public abstract getAs<T>(key: string, clazz: Class<T>, cacheName?: string): T;

	public abstract put(key: string, payload: object, cacheName?: string): boolean;

	public abstract putString(key: string, payload: string, cacheName?: string): boolean;

	public abstract putNumber(key: string, payload: number, cacheName?: string): boolean;

	public abstract remove(key: string): boolean;

	public abstract getServiceId(): string;
}



// WEBPACK FOOTER //
// ./src/core/service/cache/ILocalCacheService.ts